// assetRegistry.ts — loads the manifest, fetches GLBs on demand, and hands
// out cloned scene-graph instances. The ONLY new abstraction in the web app
// (everything else mirrors oq-images).
//
// Flow:
//   1. loadManifest()  — fetched once, cached.
//   2. getInstance(kind) — returns a fresh cloned THREE.Group with the
//      master's geometry & materials. GPU buffers are shared between clones.
//
// See AGENT.md "Source-of-truth rules" and web/CLAUDE.md "Hard rules".

import * as THREE from "three";
import { GLTFLoader } from "three-stdlib";
import { DRACOLoader } from "three-stdlib";
import { clone as skeletonClone } from "three/examples/jsm/utils/SkeletonUtils.js";
import {
  KIND_TO_MASTER,
  type SceneKind,
} from "../data/sceneKinds";

// ----- types matching tools/export_blender.py output -----

export type AssetRecord = {
  file: string;
  size_bytes: number;
  verts_pre: number;
  polys_pre: number;
  verts_post: number;
  polys_post: number;
  bbox: { min: number[]; max: number[]; size: number[] } | null;
  anchor: string;
  animations: string[];
  health_warnings: string[];
};

export type CatalogEntry = {
  kind: SceneKind;
  master: string;
  label: string;
  group: "AI" | "Infra" | "Power" | "Site" | "Network" | "Brand";
  available: boolean;
};

export type Manifest = {
  version: number;
  generated_at: string;
  blend_file: string | null;
  kindMap: Record<string, string>;
  catalog: CatalogEntry[];
  assets: Record<string, AssetRecord>;
};

// ----- module state -----

// Honour Vite's BASE_URL so the SPA works under any subpath when built with
// ONEQODE_BASE=/sandbox/ or similar. import.meta.env.BASE_URL has a trailing
// slash by convention.
const BASE_URL = (import.meta as any).env?.BASE_URL ?? "/";
const ASSETS_BASE = `${BASE_URL.replace(/\/$/, "")}/assets`;
const MANIFEST_URL = `${ASSETS_BASE}/manifest.json`;
const DRACO_PATH = `${BASE_URL.replace(/\/$/, "")}/draco/`;

let manifestPromise: Promise<Manifest> | null = null;
const masterPromises = new Map<string, Promise<THREE.Group>>();
const masterCache = new Map<string, THREE.Group>();

let loader: GLTFLoader | null = null;

function getLoader(): GLTFLoader {
  if (loader) return loader;
  const draco = new DRACOLoader();
  // Decoder files copied into web/public/draco/ from three/examples/jsm/libs/draco
  // by tools/export_blender.py is NOT responsible for these — see web/CLAUDE.md.
  // The three.js Draco loader will fetch /draco/draco_wasm_wrapper.js plus the
  // .wasm; both are bundled in public/draco/.
  draco.setDecoderPath(DRACO_PATH);
  draco.setDecoderConfig({ type: "wasm" });
  const l = new GLTFLoader();
  l.setDRACOLoader(draco);
  loader = l;
  return l;
}

// ----- public API -----

export function loadManifest(): Promise<Manifest> {
  if (!manifestPromise) {
    manifestPromise = fetch(MANIFEST_URL, { cache: "no-cache" })
      .then((r) => {
        if (!r.ok) throw new Error(`Manifest fetch failed: ${r.status}`);
        return r.json() as Promise<Manifest>;
      })
      .catch((e) => {
        console.error("[assetRegistry] manifest load failed", e);
        manifestPromise = null;
        throw e;
      });
  }
  return manifestPromise;
}

export async function getMaster(masterName: string): Promise<THREE.Group> {
  const cached = masterCache.get(masterName);
  if (cached) return cached;
  let promise = masterPromises.get(masterName);
  if (!promise) {
    promise = (async () => {
      const manifest = await loadManifest();
      const rec = manifest.assets[masterName];
      if (!rec) {
        throw new Error(`No asset for master "${masterName}" in manifest`);
      }
      const url = `${ASSETS_BASE}/${rec.file}`;
      const gltf = await getLoader().loadAsync(url);
      const root = gltf.scene;
      // Restyle every material so the look matches the Blender hero renders
      // (dark milky satin) regardless of how the GLB material exported.
      stylizeMaterials(root);
      (root as any).__gltf = gltf;
      (root as any).__masterName = masterName;
      const box = new THREE.Box3().setFromObject(root);
      (root as any).__bbox = box;
      masterCache.set(masterName, root);
      return root;
    })();
    masterPromises.set(masterName, promise);
  }
  return promise;
}

/**
 * Apply Three.js-side styling to GLB materials.
 *
 * Contract (see web/CLAUDE.md and tools/CLAUDE.md):
 *   - Blender owns the OBJECT (geometry, hierarchy, UVs, origins, naming).
 *   - Blender ships materials as named tokens only — placeholders.
 *   - Three.js owns the LOOK (colours, lighting, roughness, metalness,
 *     emissive, transparency, hover/scroll/animation).
 *
 * Token vocabulary (must match `tools/normalize_materials.py`):
 *   mat-surface  primary chassis / building shell
 *   mat-metal    metallic accents, trim
 *   mat-glass    glass, screens, water, solar panels
 *   mat-accent   brand blue active LEDs / indicators
 *   mat-emissive white indicator strips, signage
 *   mat-ground   pavement, asphalt, road slabs
 *
 * Anything else is a legacy material name and falls through to a safety net
 * that clones the GLB material with a roughness floor — ideally never hit.
 */
function stylizeMaterials(root: THREE.Object3D): void {
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const next = mats.map((m) => stylizeOne(m as THREE.MeshStandardMaterial));
    mesh.material = next.length === 1 ? next[0] : (next as any);
  });
}

// Cached restyled materials, keyed by token name. Three.js can batch draw calls
// when the same material reference is used by many meshes.
const restyleCache = new Map<string, THREE.MeshStandardMaterial>();

function stylizeOne(m: THREE.MeshStandardMaterial): THREE.MeshStandardMaterial {
  // Strip any `.NNN` suffix as a defensive measure — though the bake should
  // ship clean tokens after normalize_materials.py runs.
  const rawName = (m.name || "").trim();
  const name = rawName.replace(/\.\d+$/, "");
  const cached = restyleCache.get(name);
  if (cached) return cached;
  const out = matFor(name, m);
  out.name = name || "default";
  restyleCache.set(name, out);
  return out;
}

function matFor(name: string, src: THREE.MeshStandardMaterial): THREE.MeshStandardMaterial {
  switch (name) {
    // ---------- mat-surface: chassis / building shells / dim indicators ----------
    case "mat-surface":
      // Matte clay charcoal. High base roughness + low envMap so light
      // disperses across the form instead of bouncing off in hot spots.
      // Clearcoat is now low/rough (in mkPbr) so the milky sheen is
      // diffuse rather than glossy.
      return mkPbr(0x161719, 0.85, 0.0, 0.10, "satin");

    // ---------- mat-metal: trim, accents, metallic panels ----------
    case "mat-metal":
      // Trim still slightly metallic, but rougher so it doesn't gleam.
      return mkPbr(0x1c1e21, 0.65, 0.20, 0.18, "metal");

    // ---------- mat-glass: screens, water, solar panels ----------
    case "mat-glass":
      // Glass kept smoother because that's its character, but envMap dropped.
      return mkPbr(0x0e1320, 0.35, 0.10, 0.30, "glass");

    // ---------- mat-accent: brand-blue active LEDs / indicators ----------
    case "mat-accent":
      // Deep cyan brand blue, color === emissive so it self-illuminates. Higher
      // roughness so the painted-plastic feel is preserved; emission makes
      // it pop without being a glossy LED.
      return mkEmissivePbr(0x0b72b7, 0x064b83, 0.38, 0.65, 0.0, 0.12, "accent");

    // ---------- mat-emissive: white indicator strips, signage backlight ----------
    case "mat-emissive":
      return mkEmissive(0xffffff, 1.2);

    // ---------- mat-ground: pavement, asphalt, road slabs ----------
    case "mat-ground":
      return mkPbr(0x0a0b0d, 0.95, 0.0, 0.02, "ground");

    // ---------- legacy / unknown ----------
    default:
      // Safety net for any material name that wasn't normalised. Tame the
      // GLB values so an unknown material doesn't blow out the dark theme.
      const fallback = src.clone();
      fallback.color.setHex(0x07090d);
      fallback.metalness = Math.min(0.08, fallback.metalness);
      fallback.roughness = Math.max(0.55, Math.min(0.72, fallback.roughness));
      fallback.envMapIntensity = 0.08;
      return applyOneQodeShader(fallback, "satin");
  }
}

// MeshPhysicalMaterial keeps the clearcoat option for the milky sheen, but at
// low intensity (0.25) and high clearcoat roughness (0.7) so the lacquer is
// matte rather than glossy. Bouncing light is dispersed across the surface,
// not pooled into harsh highlights.
function mkPbr(
  color: number,
  roughness: number,
  metalness: number,
  envMapIntensity = 0.25,
  shaderPreset: OneQodeShaderPreset = "satin",
): THREE.MeshPhysicalMaterial {
  return applyOneQodeShader(new THREE.MeshPhysicalMaterial({
    color,
    roughness,
    metalness,
    envMapIntensity,
    clearcoat: 0.25,
    clearcoatRoughness: 0.70,
  }), shaderPreset);
}

function mkEmissive(color: number, strength: number): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({
    color: 0x000000,
    roughness: 1,
    metalness: 0,
    emissive: color,
    emissiveIntensity: strength,
    toneMapped: false,
  });
  return m;
}

/** PBR base + additive emissive — for surfaces that read as a solid color
 *  AND glow (brand blue LEDs, illuminated panels). */
function mkEmissivePbr(
  color: number,
  emissive: number,
  emissiveIntensity: number,
  roughness: number,
  metalness: number,
  envMapIntensity = 0.25,
  shaderPreset: OneQodeShaderPreset = "accent",
): THREE.MeshPhysicalMaterial {
  return applyOneQodeShader(new THREE.MeshPhysicalMaterial({
    color,
    roughness,
    metalness,
    envMapIntensity,
    emissive,
    emissiveIntensity,
    toneMapped: true,
    clearcoat: 0.15,
    clearcoatRoughness: 0.70,
  }), shaderPreset);
}

type OneQodeShaderPreset = "satin" | "metal" | "glass" | "accent" | "ground";

type OneQodeShaderTuning = {
  ribFrequency: number;
  ribStrength: number;
  fleckDensity: number;
  fleckStrength: number;
  topSheen: number;
  rimStrength: number;
  verticalShade: number;
};

const SHADER_TUNING: Record<OneQodeShaderPreset, OneQodeShaderTuning> = {
  satin: {
    // Matte tuning — quiet rim so the form reads as diffuse plastic rather
    // than lacquered. The two values feed the v5 fresnel rim via Math.max,
    // so dropping both lowers the wrap-around shine.
    ribFrequency: 0,
    ribStrength: 0.0,
    fleckDensity: 0.0,
    fleckStrength: 0.0,
    topSheen: 0.06,
    rimStrength: 0.05,
    verticalShade: 0.40,
  },
  metal: {
    ribFrequency: 22,
    ribStrength: 0.04,
    fleckDensity: 0.008,
    fleckStrength: 0.1,
    topSheen: 0.18,
    rimStrength: 0.1,
    verticalShade: 0.28,
  },
  glass: {
    ribFrequency: 12,
    ribStrength: 0.025,
    fleckDensity: 0.004,
    fleckStrength: 0.05,
    topSheen: 0.24,
    rimStrength: 0.18,
    verticalShade: 0.22,
  },
  accent: {
    ribFrequency: 10,
    ribStrength: 0.025,
    fleckDensity: 0.004,
    fleckStrength: 0.04,
    topSheen: 0.28,
    rimStrength: 0.18,
    verticalShade: 0.12,
  },
  ground: {
    ribFrequency: 8,
    ribStrength: 0.015,
    fleckDensity: 0.002,
    fleckStrength: 0.03,
    topSheen: 0.03,
    rimStrength: 0.015,
    verticalShade: 0.18,
  },
};

// V5 MILKY SHADER (ported from /Users/rs/dev/oq-images/src/lib/sceneAssets.ts)
//
// Two additive effects layered over the base material:
//   1. Silhouette fresnel rim — a cool highlight at grazing angles. Combined
//      with clearcoat=1.0 on the base material, this is what produces the
//      "milky" feel: the lacquer-like sheen wraps the form rather than
//      sitting only on facing-up caps.
//   2. Faint horizontal bands on side faces only — fakes architectural
//      stratified-slat detail at no extra geometry cost. Side mask is
//      `1.0 - abs(worldNormal.y)` so tops/bottoms stay clean.
//
// Reference values are tuned per-preset; "satin" matches v5's matte clay.
export function applyOneQodeShader<T extends THREE.MeshStandardMaterial>(
  material: T,
  presetName: OneQodeShaderPreset = "satin",
): T {
  const tuning = SHADER_TUNING[presetName];
  // Map old preset tunings -> v5 uniforms. topSheen → rim strength, rimStrength
  // → rim power scale, ribStrength → band strength.
  const rimStrength = Math.max(tuning.topSheen, tuning.rimStrength);
  const bandStrength = Math.max(0, tuning.ribStrength);
  const bandSpacing = 0.06;
  const rimPower = 1.8;

  material.userData.oneqodeShaderPreset = presetName;
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uOqRimColor = { value: new THREE.Color(0x4a4c50) };
    shader.uniforms.uOqRimPower = { value: rimPower };
    shader.uniforms.uOqRimStrength = { value: rimStrength };
    shader.uniforms.uOqBandColor = { value: new THREE.Color(0x2a2c30) };
    shader.uniforms.uOqBandSpacing = { value: bandSpacing };
    shader.uniforms.uOqBandStrength = { value: bandStrength };

    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        /* glsl */ `
          #include <common>
          varying vec3 vOqWorldPos;
          varying vec3 vOqWorldNormal;
        `,
      )
      .replace(
        "#include <worldpos_vertex>",
        /* glsl */ `
          #include <worldpos_vertex>
          vOqWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
          vOqWorldNormal = normalize(mat3(modelMatrix) * objectNormal);
        `,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        /* glsl */ `
          #include <common>
          uniform vec3 uOqRimColor;
          uniform float uOqRimPower;
          uniform float uOqRimStrength;
          uniform vec3 uOqBandColor;
          uniform float uOqBandSpacing;
          uniform float uOqBandStrength;
          varying vec3 vOqWorldPos;
          varying vec3 vOqWorldNormal;
        `,
      )
      .replace(
        "#include <dithering_fragment>",
        /* glsl */ `
          #include <dithering_fragment>
          // Silhouette fresnel rim — wraps form into a milky highlight
          vec3 _oq_V = normalize(vViewPosition);
          float _oq_NdotV = max(dot(normalize(vNormal), _oq_V), 0.0);
          float _oq_fres = pow(1.0 - _oq_NdotV, uOqRimPower);
          gl_FragColor.rgb += uOqRimColor * _oq_fres * uOqRimStrength;
          // Faint horizontal banding on side faces only.
          float _oq_sideMask = 1.0 - abs(vOqWorldNormal.y);
          float _oq_yPos = vOqWorldPos.y / uOqBandSpacing;
          float _oq_bandF = abs(fract(_oq_yPos) - 0.5);
          float _oq_aa = fwidth(_oq_yPos);
          float _oq_band = 1.0 - smoothstep(0.06, 0.06 + _oq_aa, _oq_bandF);
          gl_FragColor.rgb += uOqBandColor * _oq_band * _oq_sideMask * uOqBandStrength;
        `,
      );
  };
  material.customProgramCacheKey = () => `oneqode-look-v5:${presetName}`;
  return material;
}

/**
 * Return a fresh independent instance of the kind's master mesh. Clones the
 * scene graph so transforms are independent; geometry/materials are shared
 * on the GPU.
 */
export async function getInstance(kind: SceneKind): Promise<THREE.Group> {
  const master = KIND_TO_MASTER[kind];
  if (!master) throw new Error(`Unknown SceneKind "${kind}"`);
  const root = await getMaster(master);
  // SkeletonUtils.clone is safe for skinned meshes and equivalent to .clone()
  // for non-skinned. Future-proof.
  const inst = skeletonClone(root) as THREE.Group;
  inst.userData.kind = kind;
  inst.userData.master = master;
  return inst;
}

/**
 * Build a fallback placeholder when an asset is missing. Magenta wireframe
 * cube with the master name floating over it — easy to spot during dev.
 */
export function placeholder(masterName: string, sizeY = 1.5): THREE.Group {
  const g = new THREE.Group();
  g.name = `placeholder:${masterName}`;
  const geom = new THREE.BoxGeometry(0.6, sizeY, 0.6);
  const mat = new THREE.MeshBasicMaterial({
    color: 0xff00ff,
    wireframe: true,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.position.y = sizeY / 2;
  g.add(mesh);
  return g;
}

/**
 * Convenience: get an instance, or a placeholder if loading fails.
 */
export async function getInstanceOrPlaceholder(
  kind: SceneKind,
): Promise<THREE.Group> {
  try {
    return await getInstance(kind);
  } catch (e) {
    console.warn(`[assetRegistry] kind=${kind} failed, using placeholder`, e);
    return placeholder(KIND_TO_MASTER[kind] ?? kind);
  }
}

export function listAvailableKinds(manifest: Manifest): SceneKind[] {
  return manifest.catalog
    .filter((e) => e.available)
    .map((e) => e.kind as SceneKind);
}
